/* The Products section home (designs/products-pdp.md Screen 1, reshaped by
   teams-and-products.md TP3): the NR8 table over every product — Product ·
   PDP phase · Next gate · Phase complete · Last gate review · Team · Market
   · Vehicles — every cell in one of the three honest states. A product is a
   PDP track owned by a team (a team may own several, platform teams too); one
   no team owns yet says so; an Admin adds one with New product…. "Not in the PDP" is a recorded answer and shows no
   red; the red count means only "nobody has answered yet". The percent
   is the declared phase's; the review column reads the latest gate review. */
import { useEffect, useRef, useState, type FormEvent, type JSX, type KeyboardEvent } from "react";
import { ownsProducts, type CanonicalTeam } from "../../../shared/canonical-teams";
import { validateProductInput, type Product } from "../../../shared/products";
import { getTeams } from "../intake/api";
import { createProduct } from "./products.actions";
import { owedSlots, phasePercent, type ProductPdp } from "../../../shared/product-pdp";
import type { PdpGate } from "../../../shared/pdp";
import type { Role } from "../../../shared/roles";
import { useAppState } from "../shell/store";
import { ViewFrame } from "../shell/ViewFrame";
import { Loading } from "../shell/Loading";
import { LoadError } from "../shell/LoadError";
import type { ClientState } from "../shell/state";
import { fmtDay, fmtMonthYear, gateAfter, pdpFor, roleName } from "./products/pdpText";
import { DECISION_LABEL } from "./products/model";

const DASH = "—";
const Missing = ({ text = "not recorded" }: { text?: string }): JSX.Element => (
  <span className="state missing">{text}</span>
);

function PhaseCell({ pdp }: { pdp: ProductPdp }): JSX.Element {
  const d = pdp.declaration;
  if (!d) return <Missing />;
  if (d.kind === "not-in-pdp") return <span className="state not-in-pdp">not in the PDP</span>;
  return (
    <>
      Phase {d.phase} since {fmtMonthYear(d.effective)}
      {d.unconfirmed && <span className="state unconfirmed">unconfirmed</span>}
    </>
  );
}

/* PP18: `{gate label} · {approver role name}`, the approver gap red */
function NextGateCell({ gates, roles, phase }: { gates: PdpGate[]; roles: Role[]; phase: number }): JSX.Element {
  const gate = gateAfter(gates, phase);
  if (!gate) return <>{DASH}</>;
  return (
    <>
      {gate.label} · {gate.approverRoleId ? roleName(roles, gate.approverRoleId) : <Missing />}
    </>
  );
}

/* PP18: `{gate label} {decision label} · {date}` from the latest review
   recorded, `none recorded` until one is */
function LastReviewCell({ pdp, gates }: { pdp: ProductPdp; gates: PdpGate[] }): JSX.Element {
  const last = pdp.reviews.at(-1);
  if (!last) return <>none recorded</>;
  const gate = gates.find((g) => g.id === last.gateId);
  return (
    <>
      {gate?.label ?? last.gateId} {DECISION_LABEL[last.decision]} · {fmtDay(last.reviewDate)}
    </>
  );
}

/* the declared phase's PP6 number — the same one as its `.pct-row` in the
   Phase area — beside its red count (omitted at 0); `nothing owed` when
   the phase owes no row */
function PhaseCompleteCell({ pdp, catalog }: { pdp: ProductPdp; catalog: ClientState["pdp"] }): JSX.Element {
  const d = pdp.declaration;
  if (d?.kind !== "phase") return <>{DASH}</>;
  const { percent, counts } = phasePercent(owedSlots(catalog.deliverables, d.phase), pdp.evidence, d.phase);
  if (percent === null) return <>nothing owed</>;
  return (
    <>
      <span className="pct">{percent}%</span>
      {counts.missing > 0 && <Missing text={`${counts.missing} not recorded`} />}
    </>
  );
}

export function ProductsView(): JSX.Element {
  const { state, store } = useAppState();
  /* the intake team list, to name each product's owning team; best-effort —
     a failed read shows the team's id, never a wrong name */
  const [teams, setTeams] = useState<CanonicalTeam[]>([]);
  useEffect(() => {
    void getTeams()
      .then(setTeams)
      .catch(() => setTeams([]));
  }, []);
  const admin = state.me.roles.includes("admin");
  /* Cancel / Escape return focus to the button that opened the form (PM2;
     Copilot on #147) — a keyboard user is never left on <body> */
  const newRef = useRef<HTMLButtonElement>(null);
  const [returnFocus, setReturnFocus] = useState(false);
  useEffect(() => {
    if (returnFocus && state.productFormOpen !== "new") {
      newRef.current?.focus();
      setReturnFocus(false);
    }
  }, [returnFocus, state.productFormOpen]);
  if (state.productsError) return <LoadError what="products" />;
  if (state.productsPending) return <Loading what="products" />;

  const q = state.productsSearch.trim().toLowerCase();
  const shown = state.products
    .filter((p) => state.productsShowRetired || !p.retired)
    .filter((p) => !q || p.name.toLowerCase().includes(q));
  const n = state.products.length;

  return (
    <ViewFrame
      banner={
        <div className="banner">
          <b>Products.</b>
          <span>
            {n} product{n === 1 ? "" : "s"} — each owned by a team (a team may own several; platform teams own platform products), with its
            market and vehicles, measured against the PDP from one declaration; the Phase complete column is the
            declared phase's.
          </span>
        </div>
      }
      footnote={
        <span>
          A product with no declaration owes nothing until someone declares a phase; <i>not in the PDP</i> is a recorded
          answer, not a gap. A team's first product is made when it gets the product or other type on <b>Teams</b>; an
          Admin adds more with <b>New product…</b>.
        </span>
      }
    >
      {state.listError ? (
        <p className="form-error" role="alert" tabIndex={-1}>
          {state.listError}
        </p>
      ) : null}
      <div className="dash">
        {state.productFormOpen === "new" ? (
          <NewProductForm
            teams={teams}
            onClose={() => {
              setReturnFocus(true);
              store.update({ productFormOpen: null });
            }}
          />
        ) : (
        <div className="dash-top">
          <label className="dash-search">
            <span className="sr-only">Search products</span>
            <input
              id="products-search"
              type="search"
              autoComplete="off"
              placeholder="Search products…"
              value={state.productsSearch}
              onChange={(e) => store.update({ productsSearch: e.target.value })}
            />
          </label>
          <label className="field products-retired">
            <input
              type="checkbox"
              id="products-show-retired"
              checked={state.productsShowRetired}
              onChange={(e) => store.update({ productsShowRetired: e.target.checked })}
            />{" "}
            Show retired
          </label>
          {admin && (
            <button
              id="new-product"
              ref={newRef}
              className="btn primary dash-new"
              title="A new PDP track for a team — its own name, market, vehicles and PDP."
              onClick={() => store.update({ productFormOpen: "new" })}
            >
              New product…
            </button>
          )}
        </div>
        )}
        <div className="dash-table">
          <table id="products-table" className="recs">
            <thead>
              <tr>
                <th>Product</th>
                <th>PDP phase</th>
                <th>Next gate</th>
                <th>Phase complete</th>
                <th>Last gate review</th>
                <th title="The team that owns this product. A team may own several; platform teams own platform products.">Team</th>
                <th title="A grouping across product teams — the exec rollup groups by it.">Market</th>
                <th title="The product's vehicles. A product can have several; the same name may mean a different vehicle on another product.">
                  Vehicles
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td className="recs-empty" colSpan={8}>
                    {n === 0 ? (
                      <>
                        No products yet — give a team the product or other type on <b>Teams</b>.
                      </>
                    ) : (
                      "Nothing matches — clear the search."
                    )}
                  </td>
                </tr>
              ) : (
                shown.map((p) => (
                  <Row key={p.id} product={p} pdp={pdpFor(state.productPdp, p.id)} state={state} teams={teams} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </ViewFrame>
  );
}

function Row({
  product,
  pdp,
  state,
  teams,
}: {
  product: Product;
  pdp: ProductPdp;
  state: ClientState;
  teams: CanonicalTeam[];
}): JSX.Element {
  const { store } = useAppState();
  const d = pdp.declaration;
  const inPhase = d?.kind === "phase";
  return (
    <tr className="rec">
      <td>
        <button className="rec-name" onClick={() => store.openProduct(product.id)}>
          {product.name}
        </button>
        {product.retired && <span className="state retired">retired</span>}
        {product.teamId == null && (
          <span className="state unconfirmed" title="No team owns this product yet — an Admin files it with Edit product….">
            not filed under a team
          </span>
        )}
      </td>
      <td>
        <PhaseCell pdp={pdp} />
      </td>
      <td>{inPhase ? <NextGateCell gates={state.pdp.gates} roles={state.roles} phase={d.phase} /> : DASH}</td>
      <td>
        <PhaseCompleteCell pdp={pdp} catalog={state.pdp} />
      </td>
      <td>{inPhase ? <LastReviewCell pdp={pdp} gates={state.pdp.gates} /> : DASH}</td>
      <td>
        {product.teamId == null ? <Missing /> : (teams.find((t) => t.id === product.teamId)?.name ?? product.teamId)}
      </td>
      <td>{product.market ?? <Missing />}</td>
      <td>
        <VehiclesCell vehicles={state.vehicles.filter((v) => v.productId === product.id && !v.retired)} />
      </td>
    </tr>
  );
}

/* the product's live vehicles, a review flag on any a requester added */
function VehiclesCell({ vehicles }: { vehicles: ClientState["vehicles"] }): JSX.Element {
  if (vehicles.length === 0) return <Missing text="none recorded" />;
  return (
    <>
      {vehicles.map((v, i) => (
        <span key={v.id}>
          {i > 0 && ", "}
          {v.name}
          {v.needsReview && <span className="state unconfirmed">needs review</span>}
        </span>
      ))}
    </>
  );
}

/* New product… (teams-and-products.md TP2, option B), Admin: a new PDP
   track — its name, the team that owns it, its market. On
   201 the list is re-read and the record opens (createProduct). */
function NewProductForm({ teams, onClose }: { teams: CanonicalTeam[]; onClose: () => void }): JSX.Element {
  const { state, store } = useAppState();
  /* any live team with a type — platform teams own platform products */
  const owners = teams.filter(ownsProducts);
  const [name, setName] = useState("");
  const [teamId, setTeamId] = useState("");
  const [market, setMarket] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [refusals, setRefusals] = useState(0);
  const [saving, setSaving] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => firstRef.current?.focus(), []);
  useEffect(() => {
    if (refusals > 0) errorRef.current?.focus();
  }, [refusals]);
  const refuse = (reason: string): void => {
    setError(reason);
    setRefusals((r) => r + 1);
  };
  const save = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (saving) return;
    const input = { name, teamId: teamId === "" ? null : teamId, market: market.trim() === "" ? null : market.trim() };
    const sooner = validateProductInput(input, state.products, owners.map((t) => t.id));
    if (sooner) return refuse(sooner);
    setSaving(true);
    const refused = await createProduct(store, name.trim(), { teamId: input.teamId, market: input.market });
    setSaving(false);
    if (refused) refuse(refused);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && !saving) onClose();
  };
  return (
    <form id="new-product-form" className="card-form" onSubmit={(e) => void save(e)} onKeyDown={onKey}>
      <label className="field">
        Name{" "}
        <input id="new-product-name" type="text" ref={firstRef} maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field" title="The team that owns it — platform teams own platform products. A team may own several.">
        Team{" "}
        <select id="new-product-team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">— not filed under a team —</option>
          {owners.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field" title="A grouping across product teams (Construction, Ag, Landscaping…).">
        Market{" "}
        <input id="new-product-market" type="text" maxLength={100} value={market} placeholder="not recorded" onChange={(e) => setMarket(e.target.value)} />
      </label>
      <p className="form-error" role="alert" tabIndex={-1} ref={errorRef}>
        {error ?? ""}
      </p>
      <div className="record-actions">
        <button id="create-product" className="btn primary" type="submit" disabled={saving}>
          Create
        </button>
        <button id="cancel-product" className="btn ghost" type="button" onClick={onClose} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}
